import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'

interface CircularProgressProps {
  value: number
  loading: boolean
  size?: number
}

const CircularProgress = ({ value, loading, size = 220 }: CircularProgressProps) => {
  const [displayValue, setDisplayValue] = useState(0)
  const { t } = useLanguage()
  
  useEffect(() => {
    if (!loading) {
      const timer = setInterval(() => {
        setDisplayValue(prev => {
          const next = prev + 0.5
          return next >= value ? value : next
        })
      }, 50)

      return () => clearInterval(timer)
    }
  }, [loading, value])

  // 响应式尺寸计算
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640
  const actualSize = isMobile ? 180 : size
  const strokeWidth = actualSize < 200 ? 14 : 16
  const radius = (actualSize - strokeWidth * 2) / 2
  const circumference = 2 * Math.PI * radius
  const strokeOffset = circumference - (displayValue / 100) * circumference

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex flex-col items-center">
        <div 
          className="relative flex items-center justify-center"
          style={{ 
            width: actualSize, 
            height: actualSize,
          }}
        >
          <svg
            className="transform -rotate-90"
            width={actualSize}
            height={actualSize}
            viewBox={`0 0 ${actualSize} ${actualSize}`}
          >
            <defs>
              <linearGradient id={`gradient-${actualSize}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#d946ef" />
                <stop offset="50%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#0ea5e9" />
              </linearGradient>
            </defs>
            <circle
              cx={actualSize / 2}
              cy={actualSize / 2}
              r={radius}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth={strokeWidth}
            />
            <motion.circle
              cx={actualSize / 2}
              cy={actualSize / 2}
              r={radius}
              fill="none"
              stroke={`url(#gradient-${actualSize})`}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset: strokeOffset }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            {loading ? (
              <div className="flex flex-col items-center">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin mb-2"></div>
                <span className="text-sm text-gray-500">{t('chart.calculating')}</span>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <span className="block text-4xl font-bold text-gray-800">
                  {displayValue.toFixed(1)}%
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  {t('chart.aiGlobalPenetration')}
                </span>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CircularProgress 